<template>
    <div class="new-chat-container">
        <!-- Greeting / Hero section -->
        <div class="greeting-section">
            <div class="robot-container-large">
                <template v-if="guardian?.image">
                    <DotLottieVue
                        v-if="
                            typeof guardian.image === 'object' &&
                            guardian.image !== null
                        "
                        :data="guardian.image"
                        class="patty-floating"
                        loop
                        autoplay
                    />
                    <img
                        v-else
                        :src="guardian.image"
                        :alt="guardian?.name || 'Guardian'"
                        class="guardian-image-large"
                    />
                </template>
                <i v-else class="fi fi-robot guardian-placeholder-large"></i>
            </div>

            <div class="speech-bubble">
                <p class="greeting-text">
                    <strong>Hello {{ userFirstName || 'there' }}!</strong><br />
                    My name is
                    <strong>{{ guardian?.name || 'Guardian' }}</strong
                    >, and I'm your
                    <strong>{{ guardianLabel }} Expert</strong> here to assist
                    you. I can help answer any questions you have related to
                    {{ guardianLabel }} within the IPAC framework. I'm here to
                    provide you with the support you need!
                </p>
            </div>
        </div>

        <!-- Ask a question form -->
        <div class="ask-question-section">
            <i class="fi fi-info tooltip-icon" aria-label="Info">
                <span class="tooltip-text">
                    Ask any question related to {{ guardianLabel }}.
                </span>
            </i>

            <div class="ask-question-label">
                <h4 class="mb-0">Ask a question</h4>
            </div>

            <form @submit.prevent="onNewChatSubmit" class="message-form">
                <div class="message-input-wrapper">
                    <textarea
                        class="message-textarea"
                        v-model="messageInput"
                        :disabled="isViewOnlyMode"
                        :placeholder="
                            isViewOnlyMode
                                ? 'Upgrade to ask a question'
                                : 'How can we help you today?'
                        "
                        @keydown.enter.prevent="triggerFormSubmit"
                        @input="adjustTextareaHeight"
                    ></textarea>
                    <button
                        type="button"
                        class="attach-btn"
                        title="Attach file"
                    >
                        <i class="fi fi-add-square"></i>
                    </button>
                    <button
                        type="submit"
                        class="send-btn"
                        ref="sendButton"
                        :disabled="disableAskBtn"
                    >
                        <i class="fi fi-paper-plane"></i>
                    </button>
                </div>
            </form>

            <div class="expert-link-wrapper">
                <a
                    href="#"
                    class="d-inline-flex align-items-center expert-link"
                    @click.prevent="handleSpeakWithExpert"
                >
                    <span class="expert-link-text"
                        >Speak with an IPAC Expert</span
                    >
                    <i class="fi fi-chevron-right ms-1"></i>
                </a>
            </div>
        </div>

        <!-- Email IPAC Consultant Panel -->
        <SidePanel
            :visible="isContactPanelVisible"
            title="Email IPAC Consultant"
            size="panel-md"
            @close="closePanel"
        >
            <RequestForm
                :user="user"
                :success="contactFormSuccess"
                requestType="email"
                defaultCategory="IPAC_GUIDANCE_SUPPORT"
                @submit="handleFormSubmission"
            />
        </SidePanel>
    </div>
</template>

<script>
import { useGuardianStore } from '@/stores/guardian';
import { DotLottieVue } from '@lottiefiles/dotlottie-vue';
import SidePanel from '@/components/SidePanel.vue';
import RequestForm from '@/components/contact_us/RequestForm.vue';

export default {
    name: 'NewGuardianChat',
    components: {
        DotLottieVue,
        SidePanel,
        RequestForm,
    },
    props: {
        guardian: {
            type: Object,
            required: true,
        },
        showHistoryBtn: {
            type: Boolean,
            default: true,
        },
        useEvents: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['submit', 'open-history'],
    data() {
        return {
            guardianStore: null,
            messageInput: '',
            loading: false,
            isContactPanelVisible: false,
            contactFormSuccess: false,
        };
    },
    computed: {
        userFirstName() {
            return this.$userStore?.user?.first_name || this.userName || '';
        },
        userName() {
            return this.$userStore.getFullName || '';
        },
        user() {
            const user = this.$userStore?.user || {};
            return {
                name: this.$userStore.getFullName || '',
                email: user.email || '',
                phone: user.phone || '',
                company: user.company?.name || '',
            };
        },
        isViewOnlyMode() {
            return this.$userStore.isViewOnly;
        },
        disableAskBtn() {
            if (this.isViewOnlyMode && !this.$userStore.isCompanyAdmin) {
                return true;
            }
            if (!this.isViewOnlyMode && !this.messageInput) {
                return true;
            }
            return false;
        },
        guardianDescription() {
            return (
                this.guardian?.description ||
                'I can assist with any questions you have about IPAC.'
            );
        },
        guardianLabel() {
            return this.guardian?.label || 'IPAC';
        },
    },
    methods: {
        adjustTextareaHeight() {
            if (!this.$el || typeof this.$el.querySelector !== 'function') {
                return;
            }
            const textarea = this.$el.querySelector('textarea');
            if (textarea) {
                textarea.style.height = 'auto'; // Reset height to auto first
                textarea.style.height =
                    Math.min(textarea.scrollHeight + 3, 200) + 'px'; // Set to scrollHeight, but capped at 200px (max-height)
            }
        },
        triggerFormSubmit() {
            this.$refs.sendButton.click();
        },
        onNewChatSubmit() {
            if (this.isViewOnlyMode) {
                this.$userStore.togglePremiumModal(true);
                return;
            }
            this.loading = true;
            const queryText = this.messageInput.trim();
            this.messageInput = '';

            if (this.useEvents) {
                this.$emit('submit', queryText);
            } else {
                this.guardianStore.setQueryText(queryText);
                this.guardianStore.openThread(queryText, this.guardian);
            }
        },
        openChatHistory() {
            if (this.useEvents) {
                this.$emit('open-history');
            } else {
                this.guardianStore.openHistory(this.guardian);
            }
        },
        handleSpeakWithExpert() {
            this.isContactPanelVisible = true;
            this.contactFormSuccess = false;
        },
        closePanel() {
            this.isContactPanelVisible = false;
            this.contactFormSuccess = false;
        },
        handleFormSubmission(formData) {
            const endpoint = 'support-ticket/create';
            const successMessage = 'Message sent successfully';

            this.$postData(endpoint, formData).then((response) => {
                if (response.success) {
                    this.$alertStore.pushSuccess(successMessage);
                    this.contactFormSuccess = true;
                } else if (response.message) {
                    this.$alertStore.pushError(response.message);
                }
            });
        },
    },
    mounted() {
        if (!this.useEvents) {
            this.guardianStore = useGuardianStore();
        }
        this.$nextTick(() => {
            this.adjustTextareaHeight();
        });
        window.addEventListener('resize', this.adjustTextareaHeight);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.adjustTextareaHeight);
    },
};
</script>

<style scoped>
.new-chat-container {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
    background-color: #eff6f2;
    border-radius: 8px;
    padding: var(--space-5);
    align-items: flex-start;
}

/* Greeting section */
.greeting-section {
    display: flex;
    align-items: flex-start;
    gap: var(--space-2);
    position: relative;
    overflow: visible;
    width: 100%;
}

.robot-container-large {
    position: relative;
    width: 173px;
    height: 177px;
    flex-shrink: 0;
    z-index: 2;
}

.patty-floating {
    width: 100%;
    height: 100%;
}

.guardian-image-large {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.guardian-placeholder-large {
    font-size: 5rem;
    color: var(--neutral-400);
}

.speech-bubble {
    position: relative;
    flex: 1;
    background-color: #fff;
    border: 1px solid #e6e6e6;
    border-radius: 8px;
    padding: var(--space-4);
    min-width: 0;
    z-index: 1;
    width: 100%;
}

.speech-bubble::before {
    content: '';
    position: absolute;
    left: -8px;
    top: 24px;
    width: 0;
    height: 0;
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    border-right: 8px solid var(--Colours-Neutral-600, #e6e6e6);
    z-index: 1;
}

.speech-bubble::after {
    content: '';
    position: absolute;
    left: -9px;
    top: 24px;
    width: 0;
    height: 0;
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    border-right: 8px solid #e6e6e6;
    z-index: 0;
}

.greeting-text {
    margin: 0;
    font-size: 0.9375rem;
    line-height: 1.5;
    color: var(--black);
}

.greeting-text strong {
    font-weight: 600;
    color: var(--black);
}

/* Ask question section */
.ask-question-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    align-self: stretch;
    position: relative;
}

.ask-question-label {
    display: flex;
    align-items: center;
    gap: var(--space-2);
}

.tooltip-icon {
    cursor: pointer;
    color: var(--primary-400);
    position: absolute;
    top: 0;
    right: 0;
    display: inline-block;
}

.tooltip-text {
    display: flex;
    justify-content: center;
    align-items: center;
    visibility: hidden;
    position: absolute;
    background-color: var(--black);
    color: white;
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    font-size: 0.875rem;
    line-height: 1.35;
    font-weight: 400;
    font-family: Inter, sans-serif;
    max-width: 15.625rem;
    z-index: 1000;

    /* Position the tooltip above */
    bottom: calc(100% + 10px);
    left: 50%;
    transform: translateX(-50%);

    /* Add a nice fade effect */
    opacity: 0;
    transition: opacity 0.2s;
}

/* Add arrow pointing down */
.tooltip-text::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -6px;
    border-width: 6px;
    border-style: solid;
    border-color: var(--black) transparent transparent transparent;
}

/* Show tooltip on hover */
.tooltip-icon:hover .tooltip-text {
    visibility: visible;
    opacity: 1;
}

.message-form {
    display: flex;
    height: 127px;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--Spacing-space-2, 8px);
    align-self: stretch;
    width: 100%;
}

.message-input-wrapper {
    position: relative;
    background-color: var(--white);
    border: 1px solid var(--neutral-600);
    border-radius: var(--radius-default);
    padding: var(--space-3);
    padding-bottom: calc(var(--space-3) + 32px);
    min-height: 5rem;
    width: 100%;
}

.message-textarea {
    width: 100%;
    border: none;
    outline: none;
    resize: none;
    font-size: 0.9375rem;
    line-height: 1.5;
    padding: 0 40px 0 16px;
    min-height: 3rem;
    max-height: 200px;
    overflow-y: auto;
    background: transparent;
}

.message-textarea::placeholder {
    color: var(--neutral-800);
}

.message-textarea:disabled {
    background-color: var(--neutral-50);
    color: var(--neutral-600);
}

.attach-btn {
    position: absolute;
    bottom: var(--space-2);
    left: var(--space-2);
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    cursor: pointer;
    color: var(--neutral-600);
}

.attach-btn:hover {
    color: var(--primary-500);
}

.send-btn {
    position: absolute;
    bottom: var(--space-2);
    right: var(--space-2);
    width: 34px;
    height: 34px;
    display: flex;
    padding: 7px 10px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    border-radius: var(--Radius-radius-default, 8px);
    border: 1px solid #678277;
    background: #678277;
    cursor: pointer;
    color: var(--white);
}

.send-btn:hover {
    background: #5a7268;
    border-color: #5a7268;
}

.send-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.send-btn i {
    font-size: 18px;
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.expert-link-wrapper {
    display: flex;
    justify-content: flex-end;
}

/* Apply text-link styles to the link */
.expert-link {
    color: var(--primary-400);
    font-weight: 600;
    cursor: pointer;
    text-decoration: none !important;
}

/* Underline only the text using border-bottom, not the icon */
.expert-link-text {
    border-bottom: 1px solid currentColor;
    display: inline-block;
    line-height: 1.15;
}

/* Keep same color on hover */
.expert-link:hover {
    color: var(--primary-400);
    text-decoration: none !important;
}

@media (max-width: 767.98px) {
    .robot-container-large {
        width: 100px;
        height: 100px;
    }

    .guardian-placeholder-large {
        font-size: 3rem;
    }

    .speech-bubble {
        padding: var(--space-3);
    }

    .greeting-text {
        font-size: 0.875rem;
        line-height: 1.4;
    }

    .speech-bubble::before {
        left: -6px;
        top: 16px;
        border-top: 6px solid transparent;
        border-bottom: 6px solid transparent;
        border-right: 6px solid #fff;
    }

    .speech-bubble::after {
        left: -7px;
        top: 16px;
        border-top: 6px solid transparent;
        border-bottom: 6px solid transparent;
        border-right: 6px solid #e6e6e6;
    }
}
</style>
